import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import './App.css';
import TermsPage from './components/TermsPage';
import PrivacyPage from './components/PrivacyPage';
import AppIcon from './components/AppIcon';
import BetaDashboard from './components/BetaDashboard';
import BetaSignupForm from './components/BetaSignupForm';

// Home page component
function HomePage() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);
  const [showBetaDashboard, setShowBetaDashboard] = useState(false);
  const [isDark, setIsDark] = useState(false);


  // Check initial theme and listen for changes
  useEffect(() => {
    const checkTheme = () => {
      setIsDark(document.documentElement.classList.contains('dark-theme'));
    };

    // Set initial state
    checkTheme();

    // Listen for theme changes
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  const imageSuffix = isDark ? '-dark' : '-light';

  const screenshots = [
    {
      src: `/screenshots/executors${imageSuffix}.png`,
      alt: "ControllerApp Executors Interface - Sequence Control with Faders and Go+ Buttons",
      caption: "Executors Interface"
    },
    {
      src: `/screenshots/programmer-keypad${imageSuffix}.png`,
      alt: "ControllerApp Programmer Keypad - Command Input with Function Buttons and Numerical Keypad",
      caption: "Programmer Keypad"
    },
    {
      src: `/screenshots/fixture-control${imageSuffix}.png`,
      alt: "ControllerApp Fixture Control - Individual Fixture Management and Control Interface",
      caption: "Fixture Control"
    },
    {
      src: `/screenshots/color-mixer${imageSuffix}.png`,
      alt: "ControllerApp Color Mixer - Advanced Color Control and Mixing Interface",
      caption: "Color Mixer"
    },
    {
      src: `/screenshots/dipswitch${imageSuffix}.png`,
      alt: "ControllerApp Dipswitch - Configuration and Settings Management Interface",
      caption: "Dipswitch"
    },
    {
      src: `/screenshots/beam-calculator${imageSuffix}.png`,
      alt: "ControllerApp Beam Calculator - Lighting Beam Calculations and Measurements",
      caption: "Beam Calculator"
    },
    {
      src: `/screenshots/playback${imageSuffix}.png`,
      alt: "ControllerApp Playback - Show Playback and Cue Management Interface",
      caption: "Playback"
    }
  ];

  // Auto-scroll functionality
  useEffect(() => {
    if (!isAutoScrolling) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % screenshots.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [isAutoScrolling, screenshots.length]);

  // Pause auto-scroll on hover


  // Manual navigation
  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    setIsAutoScrolling(false);
    // Resume auto-scroll after 5 seconds of manual interaction
    setTimeout(() => setIsAutoScrolling(true), 5000);
  };

  const nextSlide = () => {
    goToSlide((currentIndex + 1) % screenshots.length);
  };

  const prevSlide = () => {
    goToSlide((currentIndex - 1 + screenshots.length) % screenshots.length);
  };

  // App Store URL - Universal link format (auto-redirects to user's local App Store)
  const appStoreUrl = 'https://apps.apple.com/app/id6749446742';

  return (
    <div className="app animated-dots">
      {/* Animated dots */}
      <div className="dot"></div>
      <div className="dot"></div>
      <div className="dot"></div>
      <div className="dot"></div>
      <div className="dot"></div>
      <div className="dot"></div>
      <div className="dot"></div>
      <div className="dot"></div>
      <div className="dot"></div>
      <div className="dot"></div>
      <div className="dot"></div>
      <div className="dot"></div>
      <div className="dot"></div>
      <div className="dot"></div>
      <div className="dot"></div>
      <div className="dot"></div>
      <div className="dot"></div>

      {/* Main Content */}
      <div className="main-content with-sticky-icon">
        {/* Hero Section */}
        <div id="hero-section" className="hero-section">
          {/* App Icon Component */}
          <AppIcon isSticky={true} showPlaceholder={true} />
          
          <h1 className="hero-title">
            ControllerApp
          </h1>
          
          {/* App Store Button */}
          <a
            href={appStoreUrl}
            className="app-store-button"
            aria-label="Download ControllerApp on the App Store"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src="/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg"
              alt="Download on the App Store"
              className="app-store-badge"
            />
          </a>

          {/* App Screenshots Stacked Cards */}
          <div className="stacked-cards-container">
            <div className="cards-wrapper">
              {screenshots.map((screenshot, index) => {
                const isCenter = index === currentIndex;
                const isLeft = index === (currentIndex - 1 + screenshots.length) % screenshots.length;
                const isRight = index === (currentIndex + 1) % screenshots.length;
                const isVisible = isCenter || isLeft || isRight;
                
                return (
                  <div
                    key={index}
                    className={`stacked-card ${isCenter ? 'center' : ''} ${isLeft ? 'left' : ''} ${isRight ? 'right' : ''} ${isVisible ? 'visible' : ''}`}
                    onClick={() => {
                      if (isLeft) {
                        prevSlide();
                      } else if (isRight) {
                        nextSlide();
                      } else if (isCenter) {
                        // Center card click stops auto-scroll and zooms in
                        setIsAutoScrolling(false);
                        // Resume auto-scroll after 8 seconds of manual interaction
                        setTimeout(() => setIsAutoScrolling(true), 8000);
                      }
                    }}
                    style={{
                      '--card-index': index,
                      '--total-cards': screenshots.length,
                    } as React.CSSProperties}
                  >
                    <div className="card-content">
                      <img
                        src={screenshot.src}
                        alt={screenshot.alt}
                        className="card-screenshot"
                      />
                      {/* <div className="card-overlay">
                        <h3 className="card-title">{screenshot.caption}</h3>
                        <div className="card-description">
                        </div>
                      </div> */}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Download Section */}
        <div id="download" className="beta-section">
          <h2 className="beta-title">
            📱 Download ControllerApp
          </h2>
          <div className="beta-container">
            <div className="beta-content">
              <div className="beta-icon">
                <svg className="beta-icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3>Now Available on the App Store</h3>
              <p className="beta-description">
                ControllerApp is now available on the App Store! <br />Experience professional lighting control 
                on your iPhone or iPad. <br/>Control MA2 & MA3 consoles with an intuitive, responsive interface 
                designed for stage lighting professionals.
              </p>
              <div className="beta-features">
                <div className="beta-feature">
                  <span className="beta-feature-icon">✨</span>
                  <span>Professional lighting control</span>
                </div>
                <div className="beta-feature">
                  <span className="beta-feature-icon">🔧</span>
                  <span>MA2 & MA3 support</span>
                </div>
                <div className="beta-feature">
                  <span className="beta-feature-icon">📱</span>
                  <span>Optimized for iPhone & iPad</span>
                </div>
              </div>
              <div className="beta-cta">
                <a
                  href={appStoreUrl}
                  className="app-store-button"
                  aria-label="Download ControllerApp on the App Store"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src="/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg"
                    alt="Download on the App Store"
                    className="app-store-badge"
                  />
                </a>
                
                {/* Email Contact Section */}
                <div className="discord-community">
                  <p className="discord-text">
                    Questions or need support? Contact us at{' '}
                    <a href="mailto:hi@controller-app.com" className="email-link">
                      hi@controller-app.com
                    </a>
                  </p>
                  <a 
                    href="https://app.controller-app.com/api/v1/discord-redirect" 
                    className="discord-button"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg className="discord-button-icon" fill="currentColor" viewBox="0 0 24 24" width="20" height="20">
                      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                    </svg>
                    Join Discord Community
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Android Beta Signup Section */}
        <div id="android-beta" className="beta-section">
          <h2 className="beta-title">
            🤖 Join Android Beta
          </h2>
          <div className="beta-container">
            <div className="beta-content">
              <div className="beta-icon">
                <svg className="beta-icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <h3>Android Beta Testing</h3>
              <p className="beta-description">
                Be among the first to experience ControllerApp on Android! <br />
                Sign up for our Android beta program and help shape the future of professional lighting control.
              </p>
              <BetaSignupForm />
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div id="features" className="features-grid">
          {/* Feature cards */}
          <div className="feature-card">
            <div className="icon-container">
              <svg className="feature-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
              </svg>
            </div>
            <h3>Intuitive Controls</h3>
            <p>Professional, responsive touch surface.</p>
          </div>

          <div className="feature-card">
            <div className="icon-container">
              <svg className="feature-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
              </svg>
            </div>
            <h3>Multi-Console Support</h3>
            <p>MA2 & MA3 seamless switching and configuration.</p>
          </div>

          <div className="feature-card">
            <div className="icon-container">
              <svg className="feature-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
              </svg>
            </div>
            <h3>Professional Tools Suite</h3>
            <p>Beam calculator, Color mixer, Dipswitch and more.</p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-divider"></div>
          <div className="footer-links">
            <Link to="/terms" className="footer-link">
              Terms of Service
            </Link>
            <Link to="/privacy" className="footer-link">
              Privacy Policy
            </Link>
          </div>
          <p>© 2025 ControllerApp. All rights reserved.</p>
          
          {/* Hidden admin button - triple click to show */}
          <button 
            className="admin-button"
            onClick={() => setShowBetaDashboard(true)}
            style={{ 
              opacity: 0.1, 
              background: 'none', 
              border: 'none', 
              color: 'inherit',
              cursor: 'pointer',
              fontSize: '0.75rem',
              marginTop: '1rem'
            }}
          >
            Admin
          </button>
        </div>
      </footer>

      {/* Beta Dashboard */}
      <BetaDashboard 
        isVisible={showBetaDashboard} 
        onClose={() => setShowBetaDashboard(false)} 
      />
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
      </Routes>
    </Router>
  );
}

export default App;
